import React from 'react';
import Modal from './Modal';
import Button from './Button';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

const ConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  message,
  confirmText = 'Yes, Continue',
  cancelText = 'Cancel',
  type = 'warning', // 'warning', 'success', 'danger'
  loading = false
}) => {
  if (!isOpen) return null;

  // Previously this returned Tailwind classes for the confirm button's
  // background. Button sets `background` as an INLINE style, which always beats
  // a class — so `bg-green-600`/`bg-red-600` never applied and the success and
  // danger buttons both rendered the brand gradient. Only `warning` looked
  // right, because it also passed an inline override.
  //
  // Button already has real `success` and `danger` variants, so use those.
  const getIconAndColors = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle2 size={48} color="var(--color-success)" />,
          confirmVariant: 'success',
          iconBg: 'var(--color-success-bg)'
        };
      case 'danger':
        return {
          icon: <X size={48} color="var(--color-danger)" />,
          confirmVariant: 'danger',
          iconBg: 'var(--color-danger-bg)'
        };
      case 'warning':
      default:
        return {
          icon: <AlertTriangle size={48} color="var(--color-warning)" />,
          confirmVariant: 'warning',
          iconBg: 'var(--color-warning-bg-strong)'
        };
    }
  };

  const { icon, confirmVariant, iconBg } = getIconAndColors();

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title} maxWidth="max-w-md">
      <div className="text-center">
        {/* Icon */}
        <div
          className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center"
          style={{ backgroundColor: iconBg }}
        >
          {icon}
        </div>

        {/* Message */}
        <p className="text-sm mb-6 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {message}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelText}
          </Button>

          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                />
                Processing...
              </div>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;